/**
 * Design Tokens
 * ─────────────────────────────────────────────────────────────
 * Single source of truth for all visual primitives.
 * Change here → app-wide consistency.
 *
 * Never use magic numbers in components. Always reference these.
 */

// ─── Spacing scale (4px base) ───
export const space = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
} as const;

export type SpaceKey = keyof typeof space;

// ─── Border radius ───
// More differentiated than before: buttons should be less rounded than cards.
export const radius = {
  none: 0,
  xs: 6,    // subtle rounding (chips, tags)
  sm: 8,    // inputs, buttons (sm)
  md: 10,   // default buttons
  lg: 12,   // prominent buttons, inner cards
  xl: 16,   // cards
  '2xl': 20, // hero/modal surfaces
  full: 9999,
} as const;

export type RadiusKey = keyof typeof radius;

// ─── Typography scale ───
export const fontSize = {
  xs: 10,
  sm: 11,
  base: 13,
  md: 14,
  lg: 16,
  xl: 18,
  '2xl': 22,
  '3xl': 28,
  '4xl': 32,
  '5xl': 48,
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
} as const;

export const letterSpacing = {
  tight: -2,
  tighter: -0.5,
  normal: 0,
  wide: 1,
  wider: 1.2,
} as const;

// ─── Motion ───
export const motion = {
  duration: {
    fast: 150,
    base: 250,
    slow: 500,
    slower: 700,
  },
  easing: {
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  },
} as const;

// ─── Component sizes ───
export const size = {
  button: {
    sm: 36,
    md: 44,
    lg: 52,
  },
  icon: {
    sm: 14,
    md: 18,
    lg: 22,
    xl: 28,
  },
  tap: 44, // Minimum tap target (iOS guideline)
} as const;

// ─── Shadows ───
// In dark modes we use transparent/none instead of colored shadows.
// Shadow strings are per-mode in ColorPalette.shadow.
export const shadow = {
  none: 'none',
  sm: '0 1px 2px rgba(0,0,0,0.04)',
  md: '0 2px 14px rgba(0,0,0,0.06)',
  lg: '0 4px 24px rgba(0,0,0,0.10)',
} as const;

// ─── Z-index stack ───
export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 200,
  modal: 300,
  toast: 400,
} as const;
